/* =========================================================
   bosscorp-theme — global.js
   Vanilla JS web components. No framework, performance-first.
   ========================================================= */
(function () {
  'use strict';

  const routes = (window.bosscorp && window.bosscorp.routes) || {};
  const strings = (window.bosscorp && window.bosscorp.strings) || {};

  /* ---------- Money formatting ---------- */
  function formatMoney(cents) {
    const format = (window.bosscorp && window.bosscorp.moneyFormat) || '${{amount}}';
    const value = (cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return format.replace(/\{\{\s*amount[^}]*\}\}/, value);
  }
  window.bosscorp = window.bosscorp || {};
  window.bosscorp.formatMoney = formatMoney;

  /* ---------- Cart API helper ---------- */
  const Cart = {
    async add(items) {
      const res = await fetch(routes.cart_add_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items })
      });
      if (!res.ok) throw await res.json();
      await this.refresh();
      return res.json();
    },
    async change(line, quantity) {
      const res = await fetch(routes.cart_change_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ line, quantity })
      });
      const cart = await res.json();
      this.render(cart);
      return cart;
    },
    async get() {
      const res = await fetch(routes.cart_url + '.js', { headers: { 'Accept': 'application/json' } });
      return res.json();
    },
    async refresh() {
      const cart = await this.get();
      this.render(cart);
      return cart;
    },
    render(cart) {
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        el.hidden = cart.item_count === 0;
      });
      const drawer = document.querySelector('cart-drawer');
      if (drawer) drawer.renderCart(cart);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    }
  };
  window.bosscorp.Cart = Cart;

  /* ---------- Drawer base (open/close + focus trap + esc) ---------- */
  class DrawerElement extends HTMLElement {
    connectedCallback() {
      this.overlay = this.querySelector('.drawer__overlay');
      this.closeBtns = this.querySelectorAll('[data-drawer-close]');
      if (this.overlay) this.overlay.addEventListener('click', () => this.close());
      this.closeBtns.forEach((b) => b.addEventListener('click', () => this.close()));
      this._onKey = (e) => { if (e.key === 'Escape') this.close(); };
    }
    open() {
      this.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', this._onKey);
      const focusable = this.querySelector('input, button, a');
      if (focusable) setTimeout(() => focusable.focus(), 100);
    }
    close() {
      this.classList.remove('is-open');
      document.body.style.overflow = '';
      document.removeEventListener('keydown', this._onKey);
    }
  }

  /* ---------- Cart drawer ---------- */
  class CartDrawer extends DrawerElement {
    connectedCallback() {
      super.connectedCallback();
      document.querySelectorAll('[data-cart-toggle]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          if (window.bosscorp.cartType === 'drawer') { e.preventDefault(); this.open(); }
        });
      });
    }
    renderCart(cart) {
      const body = this.querySelector('[data-cart-body]');
      const footer = this.querySelector('[data-cart-footer]');
      if (!body) return;
      if (cart.item_count === 0) {
        body.innerHTML = `<div class="cart__empty"><p>${this.dataset.emptyText || 'Your cart is empty'}</p><a class="button button--outline mt-2" href="${routes.cart_url || '/'}">${this.dataset.continueText || 'Continue shopping'}</a></div>`;
        if (footer) footer.hidden = true;
        return;
      }
      if (footer) footer.hidden = false;
      body.innerHTML = cart.items.map((item, i) => this.lineHTML(item, i + 1)).join('');
      const subtotal = this.querySelector('[data-cart-subtotal]');
      if (subtotal) subtotal.textContent = formatMoney(cart.total_price);
    }
    lineHTML(item, line) {
      const img = item.image ? `<img src="${item.image.replace(/(\.[^.]+)$/, '_160x$1')}" alt="${item.product_title}" loading="lazy">` : '';
      const variant = item.variant_title && item.variant_title !== 'Default Title' ? `<div class="cart-item__variant">${item.variant_title}</div>` : '';
      return `<div class="cart-item">
        <a class="cart-item__image media" href="${item.url}">${img}</a>
        <div>
          <a class="cart-item__title" href="${item.url}">${item.product_title}</a>
          ${variant}
          <div class="cart-item__bottom">
            <div class="quantity">
              <button data-line-qty data-line="${line}" data-qty="-1" aria-label="Decrease">&minus;</button>
              <span data-qty-value>${item.quantity}</span>
              <button data-line-qty data-line="${line}" data-qty="1" aria-label="Increase">+</button>
            </div>
            <div><strong>${formatMoney(item.final_line_price)}</strong></div>
          </div>
          <button class="cart-item__remove" data-line-remove data-line="${line}">${this.dataset.removeText || 'Ukloni'}</button>
        </div>
      </div>`;
    }
  }

  /* ---------- Menu drawer (mobile) ---------- */
  class MenuDrawer extends DrawerElement {
    connectedCallback() {
      super.connectedCallback();
      document.querySelectorAll('[data-menu-toggle]').forEach((btn) =>
        btn.addEventListener('click', (e) => { e.preventDefault(); this.open(); }));
    }
  }

  /* ---------- Quantity input ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelectorAll('button').forEach((btn) =>
        btn.addEventListener('click', () => {
          const step = btn.dataset.qty === 'up' ? 1 : -1;
          const min = parseInt(this.input.min || '1', 10);
          this.input.value = Math.max(min, (parseInt(this.input.value, 10) || min) + step);
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        }));
    }
  }

  /* ---------- Product form (variants + add to cart + gallery) ---------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.variantInput = this.querySelector('[name="id"]');
      this.submitBtn = this.querySelector('[data-add-to-cart]');
      // price block lives outside <product-form>, so query the document
      this.priceTarget = document.querySelector('[data-price-target]');
      this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
      this.optionInputs = this.querySelectorAll('[data-option-input]');

      this.optionInputs.forEach((input) =>
        input.addEventListener('change', () => this.onOptionChange()));

      if (this.form) this.form.addEventListener('submit', (e) => this.onSubmit(e));
      this.onOptionChange(true);
    }

    getSelectedOptions() {
      const selected = [];
      this.querySelectorAll('[data-option-position]').forEach((group) => {
        const pos = +group.dataset.optionPosition;
        const checked = group.querySelector('[data-option-input]:checked') || group.querySelector('[data-option-input]');
        selected[pos - 1] = checked ? checked.value : null;
      });
      return selected;
    }

    onOptionChange(initial) {
      const groups = this.querySelectorAll('[data-option-position]');
      let match;
      if (groups.length === 0) {
        // product with only the default variant (no options) — use the single variant
        match = this.variants[0];
      } else {
        const selected = this.getSelectedOptions();
        match = this.variants.find((v) => v.options.every((opt, i) => opt === selected[i]));
      }
      this.currentVariant = match;

      if (match) {
        this.variantInput.value = match.id;
        if (!initial) this.updateURL(match.id);
        if (this.priceTarget) this.priceTarget.innerHTML = this.priceHTML(match);
        this.updateGallery(match);
      }
      this.updateButton(match);
    }

    priceHTML(v) {
      if (v.compare_at_price && v.compare_at_price > v.price) {
        return `<span class="price price--on-sale"><span class="price__sale">${formatMoney(v.price)}</span> <s class="price__compare">${formatMoney(v.compare_at_price)}</s></span>`;
      }
      return `<span class="price">${formatMoney(v.price)}</span>`;
    }

    updateButton(v) {
      if (!this.submitBtn) return;
      if (!v) { this.submitBtn.disabled = true; this.submitBtn.querySelector('span').textContent = strings.unavailable; }
      else if (!v.available) { this.submitBtn.disabled = true; this.submitBtn.querySelector('span').textContent = strings.soldOut; }
      else { this.submitBtn.disabled = false; this.submitBtn.querySelector('span').textContent = strings.addToCart; }
    }

    updateGallery(v) {
      if (!v.featured_media) return;
      const gallery = document.querySelector('media-gallery');
      if (gallery) gallery.setActiveMedia(v.featured_media.id);
    }

    updateURL(id) {
      if (!this.dataset.url) return;
      const url = new URL(window.location);
      url.searchParams.set('variant', id);
      window.history.replaceState({}, '', url);
    }

    async onSubmit(e) {
      e.preventDefault();
      if (!this.currentVariant || !this.currentVariant.available) return;
      const btn = this.submitBtn;
      btn.classList.add('is-loading');
      btn.disabled = true;
      const qtyInput = this.querySelector('[name="quantity"]');
      try {
        await Cart.add([{ id: this.currentVariant.id, quantity: qtyInput ? +qtyInput.value : 1 }]);
        if (window.bosscorp.cartType === 'drawer') {
          const drawer = document.querySelector('cart-drawer');
          if (drawer) drawer.open();
        } else {
          window.location.href = routes.cart_url;
        }
      } catch (err) {
        console.error('Add to cart failed', err);
        alert((err && err.description) || 'Could not add to cart');
      } finally {
        btn.classList.remove('is-loading');
        btn.disabled = false;
      }
    }
  }

  /* ---------- Media gallery ---------- */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.main = this.querySelector('[data-gallery-main]');
      this.thumbs = this.querySelectorAll('[data-gallery-thumb]');
      this.thumbs.forEach((t) => t.addEventListener('click', () => this.setActiveMedia(t.dataset.mediaId)));
    }
    setActiveMedia(mediaId) {
      const target = this.querySelector(`[data-media-item="${mediaId}"]`);
      if (target && this.main) {
        this.main.querySelectorAll('[data-media-item]').forEach((el) => el.hidden = true);
        target.hidden = false;
      }
      this.thumbs.forEach((t) => t.setAttribute('aria-current', t.dataset.mediaId === String(mediaId)));
    }
  }

  /* ---------- Cart line qty / remove (works in drawer AND on cart page) ---------- */
  document.addEventListener('click', async (e) => {
    const remove = e.target.closest('[data-line-remove]');
    const qtyBtn = e.target.closest('[data-line-qty]');
    if (!remove && !qtyBtn) return;
    e.preventDefault();
    const onCartPage = document.body.classList.contains('template-cart');
    let line, quantity;
    if (remove) {
      line = +remove.dataset.line;
      quantity = 0;
    } else {
      line = +qtyBtn.dataset.line;
      const valueEl = qtyBtn.parentElement.querySelector('[data-qty-value]');
      const current = valueEl ? parseInt(valueEl.textContent, 10) : 1;
      quantity = Math.max(0, current + (+qtyBtn.dataset.qty));
    }
    await Cart.change(line, quantity);
    // Cart page renders line items server-side, so reload to reflect changes
    if (onCartPage) window.location.reload();
  });

  /* ---------- Quick add (product card) ---------- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();
    const id = btn.dataset.quickAdd;
    if (!id) return;
    btn.classList.add('is-loading');
    btn.disabled = true;
    try {
      await Cart.add([{ id: +id, quantity: 1 }]);
      const drawer = document.querySelector('cart-drawer');
      if (drawer && window.bosscorp.cartType === 'drawer') drawer.open();
    } catch (err) {
      console.error(err);
    } finally {
      btn.classList.remove('is-loading');
      btn.disabled = false;
    }
  });

  /* ---------- Predictive search ---------- */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-results]');
      this.modal = this.closest('.search-modal') || this;
      this._timer = null;
      if (this.input) this.input.addEventListener('input', () => this.onInput());
      document.querySelectorAll('[data-search-toggle]').forEach((btn) =>
        btn.addEventListener('click', (e) => { e.preventDefault(); this.openModal(); }));
      const overlay = this.querySelector('.search-modal__overlay, [data-search-close]');
      this.querySelectorAll('[data-search-close], .search-modal__overlay').forEach((el) =>
        el.addEventListener('click', () => this.closeModal()));
    }
    openModal() { this.modal.classList.add('is-open'); document.body.style.overflow = 'hidden'; if (this.input) setTimeout(() => this.input.focus(), 60); }
    closeModal() { this.modal.classList.remove('is-open'); document.body.style.overflow = ''; }
    onInput() {
      clearTimeout(this._timer);
      const q = this.input.value.trim();
      if (q.length < 2) { this.results.innerHTML = ''; return; }
      this._timer = setTimeout(() => this.search(q), 250);
    }
    async search(q) {
      const url = `${routes.predictive_search_url}?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6&section_id=predictive-search`;
      try {
        const res = await fetch(url);
        const text = await res.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        // section_id rendering returns the section wrapped in #shopify-section-*
        const section = doc.querySelector('#shopify-section-predictive-search') || doc.body;
        this.results.innerHTML = section ? section.innerHTML : '';
      } catch (err) { console.error('search failed', err); }
    }
  }

  /* ---------- Slideshow ---------- */
  class SlideshowComponent extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.slideshow__track');
      this.slides = Array.from(this.querySelectorAll('.slide'));
      this.dots = Array.from(this.querySelectorAll('.slideshow__dot'));
      this.index = 0;
      this.autoplay = this.dataset.autoplay === 'true';
      this.interval = parseInt(this.dataset.speed || '5', 10) * 1000;
      this.dots.forEach((d, i) => d.addEventListener('click', () => this.goTo(i)));
      this.track.addEventListener('scroll', () => this.onScroll(), { passive: true });
      if (this.autoplay && this.slides.length > 1) this.start();
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => { if (this.autoplay) this.start(); });
    }
    goTo(i) {
      this.index = (i + this.slides.length) % this.slides.length;
      this.track.scrollTo({ left: this.slides[this.index].offsetLeft, behavior: 'smooth' });
      this.updateDots();
    }
    onScroll() {
      const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
      if (i !== this.index) { this.index = i; this.updateDots(); }
    }
    updateDots() { this.dots.forEach((d, i) => d.setAttribute('aria-current', i === this.index)); }
    start() { this.stop(); this._t = setInterval(() => this.goTo(this.index + 1), this.interval); }
    stop() { clearInterval(this._t); }
  }

  /* ---------- Scroll reveal ---------- */
  function initReveal() {
    if (!document.body.classList.contains('reveal-enabled')) return;
    const els = document.querySelectorAll('[data-reveal]');
    if (!('IntersectionObserver' in window) || !els.length) {
      els.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) { entry.target.classList.add('is-visible'); io.unobserve(entry.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    els.forEach((el) => io.observe(el));
  }

  /* ---------- Register ---------- */
  function define(name, cls) { if (!customElements.get(name)) customElements.define(name, cls); }
  define('cart-drawer', CartDrawer);
  define('menu-drawer', MenuDrawer);
  define('quantity-input', QuantityInput);
  define('product-form', ProductForm);
  define('media-gallery', MediaGallery);
  define('predictive-search', PredictiveSearch);
  define('slideshow-component', SlideshowComponent);

  if (document.readyState !== 'loading') initReveal();
  else document.addEventListener('DOMContentLoaded', initReveal);

  // Re-init reveal when Shopify theme editor loads a section
  document.addEventListener('shopify:section:load', initReveal);
})();
